import React, { useEffect, useMemo, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.insightos.co.uk';

const TABLE_COLUMNS = [
  { key: 'User_Name', label: 'Email', type: 'text', readOnly: true },
  { key: 'Name', label: 'Name', type: 'text' },
  { key: 'TradingName', label: 'Trading Name', type: 'text' },
  { key: 'Country', label: 'Country', type: 'text' },
  { key: 'Telephone', label: 'Telephone', type: 'text' },
  { key: 'StartDate', label: 'Start Date', type: 'date' },
  { key: 'Sales', label: 'Sales', type: 'number' },
  { key: 'MRR', label: 'MRR', type: 'number' },
  { key: 'LeadsWorked', label: 'Leads Worked', type: 'number' },
  { key: 'BonusCommission', label: 'Bonus Commission', type: 'number' },
  { key: 'RecurringCommisson', label: 'Recurring Commission', type: 'number' },
  { key: 'OutstandingBonus', label: 'Outstanding Bonus', type: 'number' },
  { key: 'ContractSigned', label: 'Contract Signed', type: 'checkbox' },
  { key: 'CopyContract', label: 'Contract Link', type: 'textarea' },
  { key: 'EmailPassword', label: 'Email Password', type: 'text' },
  { key: 'Host', label: 'Host', type: 'text' },
  { key: 'Port', label: 'Port', type: 'number' },
];

const NUMERIC_FIELDS = new Set([
  'Sales',
  'MRR',
  'BonusCommission',
  'RecurringCommisson',
  'LeadsWorked',
  'OutstandingBonus',
  'Port',
]);

const INITIAL_FORM = {
  User_Name: '',
  Password: '',
  EmailPassword: '',
  Name: '',
  TradingName: '',
  Country: '',
  Telephone: '',
  StartDate: '',
};

const normalizePayload = (data, { includePassword } = {}) => {
  const payload = { ...data };
  TABLE_COLUMNS.forEach(({ key }) => {
    if (NUMERIC_FIELDS.has(key)) {
      payload[key] = payload[key] === '' || payload[key] === null || payload[key] === undefined
        ? null
        : Number(payload[key]);
    }
  });
  if (Object.prototype.hasOwnProperty.call(payload, 'ContractSigned')) {
    payload.ContractSigned = payload.ContractSigned ? 1 : 0;
  }
  if (!payload.StartDate) {
    payload.StartDate = null;
  }
  if (!includePassword) {
    delete payload.Password;
  }
  return payload;
};

const Salespeople = () => {
  const [rows, setRows] = useState([]);
  const [topPerformers, setTopPerformers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchSalespeople = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/sales/salespeople`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.message || 'Failed to load salespeople');
      }
      setRows(Array.isArray(payload?.data) ? payload.data : []);
      setTopPerformers(Array.isArray(payload?.topPerformers) ? payload.topPerformers : []);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Unable to fetch salespeople');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalespeople();
  }, []);

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      if (!form.User_Name || !form.Password || !form.EmailPassword) {
        throw new Error('Email, password, and email password are required');
      }
      const payload = normalizePayload({ ...form }, { includePassword: true });
      const response = await fetch(`${API_BASE}/sales/salespeople`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || 'Failed to create salesperson');
      }
      setSuccess(data?.message || 'Salesperson created successfully');
      setForm(INITIAL_FORM);
      setCreating(false);
      fetchSalespeople();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Unable to create salesperson');
    } finally {
      setSaving(false);
    }
  };

  const startEditing = (row) => {
    setEditingUser(row.User_Name);
    setEditForm({
      ...row,
      ContractSigned: Boolean(row.ContractSigned),
      Password: '',
    });
  };

  const cancelEditing = () => {
    setEditingUser(null);
    setEditForm({});
  };

  const handleEditChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const saveEdit = async (userName) => {
    if (!userName) {
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = normalizePayload({ ...editForm }, { includePassword: Boolean(editForm.Password) });
      const response = await fetch(`${API_BASE}/sales/salespeople/${encodeURIComponent(userName)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || 'Failed to update salesperson');
      }
      setSuccess(data?.message || 'Salesperson updated');
      cancelEditing();
      fetchSalespeople();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Unable to update salesperson');
    } finally {
      setSaving(false);
    }
  };

  const deleteSalesperson = async (userName) => {
    if (!userName) {
      return;
    }
    const confirmDelete = window.confirm(`Delete ${userName}? This action cannot be undone.`);
    if (!confirmDelete) {
      return;
    }
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${API_BASE}/sales/salespeople/${encodeURIComponent(userName)}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || 'Failed to delete salesperson');
      }
      setSuccess(data?.message || 'Salesperson deleted');
      if (editingUser === userName) {
        cancelEditing();
      }
      fetchSalespeople();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Unable to delete salesperson');
    }
  };

  const topFive = useMemo(() => {
    if (topPerformers.length) {
      return topPerformers;
    }
    return [...rows]
      .sort((a, b) => (
        (Number(b.LeadsWorked) || 0) - (Number(a.LeadsWorked) || 0)
        || (Number(b.Sales) || 0) - (Number(a.Sales) || 0)
        || (Number(b.MRR) || 0) - (Number(a.MRR) || 0)
      ))
      .slice(0, 5);
  }, [rows, topPerformers]);

  const renderCell = (row, column) => {
    const editing = editingUser === row.User_Name;
    const value = editing ? editForm[column.key] : row[column.key];

    if (!editing) {
      if (column.type === 'checkbox') {
        return value ? 'Yes' : 'No';
      }
      if (value === null || value === undefined || value === '') {
        return '-';
      }
      if (column.type === 'number') {
        return Number(value).toLocaleString();
      }
      return value;
    }

    if (column.type === 'checkbox') {
      return (
        <input
          type="checkbox"
          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          checked={Boolean(value)}
          onChange={(event) => handleEditChange(column.key, event.target.checked)}
        />
      );
    }

    if (column.type === 'textarea') {
      return (
        <textarea
          className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          rows={2}
          value={value || ''}
          onChange={(event) => handleEditChange(column.key, event.target.value)}
        />
      );
    }

    return (
      <input
        type={column.type === 'number' ? 'number' : column.type === 'date' ? 'date' : 'text'}
        className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
        value={value ?? ''}
        onChange={(event) => handleEditChange(column.key, event.target.value)}
        disabled={column.readOnly}
      />
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Salespeople</h1>
          <p className="text-sm text-gray-600">Manage the sales team, contracts, and compensation in one place.</p>
        </div>
        <button
          type="button"
          onClick={() => setCreating((prev) => !prev)}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-500"
        >
          {creating ? 'Close' : 'Create Salesperson'}
        </button>
      </div>

      {creating && (
        <form onSubmit={handleCreate} className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className="text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
                value={form.User_Name}
                onChange={(event) => handleFormChange('User_Name', event.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Password</label>
              <input
                type="password"
                className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
                value={form.Password}
                onChange={(event) => handleFormChange('Password', event.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Email Password</label>
              <input
                type="text"
                className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
                value={form.EmailPassword}
                onChange={(event) => handleFormChange('EmailPassword', event.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Name</label>
              <input
                type="text"
                className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                value={form.Name}
                onChange={(event) => handleFormChange('Name', event.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Trading Name</label>
              <input
                type="text"
                className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                value={form.TradingName}
                onChange={(event) => handleFormChange('TradingName', event.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Country</label>
              <input
                type="text"
                className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                value={form.Country}
                onChange={(event) => handleFormChange('Country', event.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Telephone</label>
              <input
                type="text"
                className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                value={form.Telephone}
                onChange={(event) => handleFormChange('Telephone', event.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Start Date</label>
              <input
                type="date"
                className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                value={form.StartDate}
                onChange={(event) => handleFormChange('StartDate', event.target.value)}
              />
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={saving}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-500 disabled:opacity-60"
            >
              {saving ? 'Saving...' : 'Save Salesperson'}
            </button>
            <button
              type="button"
              onClick={() => {
                setForm(INITIAL_FORM);
                setCreating(false);
              }}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {success && (
        <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          {success}
        </div>
      )}
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div>
        <h2 className="text-lg font-semibold text-gray-900">Top Performers</h2>
        {topFive.length ? (
          <div className="mt-3 grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            {topFive.map((person, index) => (
              <div key={person.User_Name || index} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">#{index + 1}</p>
                <p className="mt-1 text-lg font-bold text-gray-900">
                  {person.Name || person.TradingName || person.User_Name}
                </p>
                <p className="text-sm text-gray-600">{person.TradingName || person.Country || '—'}</p>
                <dl className="mt-4 grid grid-cols-3 gap-2 text-center text-xs text-gray-600">
                  <div>
                    <dt className="text-gray-500">Leads</dt>
                    <dd className="text-base font-semibold text-gray-900">{person.LeadsWorked ?? 0}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Sales</dt>
                    <dd className="text-base font-semibold text-gray-900">{person.Sales ?? 0}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">MRR</dt>
                    <dd className="text-base font-semibold text-gray-900">£{Number(person.MRR ?? 0).toLocaleString()}</dd>
                  </div>
                </dl>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-2 text-sm text-gray-500">No performance data yet.</p>
        )}
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <div className="px-6 py-10 text-center text-sm text-gray-500">Loading salespeople...</div>
        ) : rows.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-gray-500">No salespeople found.</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {TABLE_COLUMNS.map((column) => (
                  <th
                    key={column.key}
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600"
                  >
                    {column.label}
                  </th>
                ))}
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => {
                const editing = editingUser === row.User_Name;
                return (
                  <tr key={row.User_Name} className={editing ? 'bg-indigo-50/40' : undefined}>
                    {TABLE_COLUMNS.map((column) => (
                      <td key={column.key} className="px-4 py-3 align-top text-gray-900">
                        {renderCell(row, column)}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right">
                      {editing ? (
                        <div className="space-y-2">
                          <input
                            type="password"
                            placeholder="New password (optional)"
                            className="w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            value={editForm.Password || ''}
                            onChange={(event) => handleEditChange('Password', event.target.value)}
                          />
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => saveEdit(row.User_Name)}
                              disabled={saving}
                              className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow hover:bg-indigo-500 disabled:opacity-60"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={cancelEditing}
                              className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => startEditing(row)}
                            className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteSalesperson(row.User_Name)}
                            className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow hover:bg-red-500"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Salespeople;
